import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Send, Sparkles, Trash2, Upload } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components";
import { toast } from "@/components/ui/use-toast";
import { useConfirmDialog } from "@/context";
import { useScopedPartnerNav } from "@/context/auth/scoped-partner-nav";
import { useI18n } from "@/context/i18n";

import {
  getCampaignAnalyticsSummary,
  getCampaignDispatchRuns,
} from "./campaign-details.api";
import { useCampaignDetails } from "./campaign-details.hooks";
import type { TCampaignContact } from "./campaign-details.types";
import * as campaignsApi from "./campaigns.api";
import { campaignsFormSchema, TCampaignsFormData } from "./campaigns.schema";
import type { TCampaignWhatsappDispatchPayload } from "./campaigns.types";
import { mapCampaignFormToUpdatePayload } from "./campaigns.utils";
import {
  CampaignDashboard,
  CampaignDetailsContactsTable,
  CampaignFlowChat,
  CampaignForm,
  CampaignMarketingCostSheet,
  CampaignUploadLeadsModal,
  CampaignWhatsappDispatchDialog,
} from "./components";

export const CampaignDetailsPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openDialog } = useConfirmDialog();
  const { clientId, campaignId } = useParams<{
    clientId: string;
    campaignId: string;
  }>();
  const { partnerHomePath } = useScopedPartnerNav(clientId);
  const [isUploadModalOpen, setIsUploadModalOpen] = React.useState(false);
  const [isEditSheetOpen, setIsEditSheetOpen] = React.useState(false);
  const [isDispatchDialogOpen, setIsDispatchDialogOpen] = React.useState(false);
  const [isCostSheetOpen, setIsCostSheetOpen] = React.useState(false);
  const [funnelDays, setFunnelDays] = React.useState(7);
  const [selectedContactForChat, setSelectedContactForChat] =
    React.useState<TCampaignContact | null>(null);
  /** Nova a cada clique em "Testar fluxo"; isola sessão no agente e força remount do chat. */
  const [flowTestSessionId, setFlowTestSessionId] = React.useState<string | null>(null);
  const {
    campaign,
    contacts,
    isCampaignLoading,
    isContactsLoading,
    enrichContacts,
    isEnriching,
  } = useCampaignDetails(campaignId);

  const analyticsRange = React.useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - funnelDays);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [funnelDays]);

  const {
    data: analyticsSummary,
    isLoading: isAnalyticsLoading,
    refetch: refetchAnalytics,
  } = useQuery({
    queryKey: [
      "campaign-analytics-summary",
      campaignId,
      analyticsRange.from,
      analyticsRange.to,
    ],
    queryFn: () =>
      getCampaignAnalyticsSummary(campaignId as string, analyticsRange),
    enabled: Boolean(campaignId),
  });

  const {
    data: dispatchRuns = [],
    isLoading: isDispatchRunsLoading,
    refetch: refetchDispatchRuns,
  } = useQuery({
    queryKey: [
      "campaign-dispatch-runs",
      campaignId,
      analyticsRange.from,
      analyticsRange.to,
    ],
    queryFn: () =>
      getCampaignDispatchRuns(campaignId as string, analyticsRange),
    enabled: Boolean(campaignId),
  });

  /** Rota /clients/:clientId define o tenant do painel; priorizar sobre campaign.organization_id (pode divergir ou vir null). */
  const organizationId =
    clientId?.trim() ||
    campaign?.organization_id?.trim() ||
    undefined;

  const editForm = useForm<TCampaignsFormData>({
    resolver: zodResolver(campaignsFormSchema),
    defaultValues: {
      name: "",
      project_id: "",
      plan_ids: [],
      channel: "whatsapp",
      status: "draft",
      organization_id: organizationId,
    },
  });

  const selectedProjectId = editForm.watch("project_id");

  const { data: editProjects = [], isLoading: isEditProjectsLoading } = useQuery({
    queryKey: ["campaigns-projects", organizationId],
    queryFn: () => campaignsApi.getProjectsByOrg(organizationId || ""),
    enabled: Boolean(organizationId) && isEditSheetOpen,
  });

  const { data: editPlans = [], isLoading: isEditPlansLoading } = useQuery({
    queryKey: ["campaigns-plans", selectedProjectId],
    queryFn: () => campaignsApi.getPlansByProject(selectedProjectId),
    enabled: Boolean(selectedProjectId) && isEditSheetOpen,
  });

  React.useEffect(() => {
    if (!isEditSheetOpen || !campaign) return;
    editForm.reset({
      name: campaign.name,
      project_id: campaign.project_id,
      plan_ids: campaign.plan_ids ?? [],
      channel: "whatsapp",
      status: campaign.status,
      organization_id: organizationId || campaign.organization_id || undefined,
    });
  }, [isEditSheetOpen, campaign, organizationId, editForm]);

  const updateCampaignMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: ReturnType<typeof mapCampaignFormToUpdatePayload>;
    }) => campaignsApi.updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-details", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["campaign-details-contacts", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast({
        title: t("generic.success"),
        description: t("campaigns.success.updated"),
      });
      setIsEditSheetOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: campaignsApi.deleteCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast({
        title: t("generic.success"),
        description: t("campaigns.success.deleted"),
      });
      if (clientId) {
        navigate(`/clients/${clientId}/campaigns`);
      } else {
        navigate("/clients");
      }
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleEnrich = React.useCallback(() => {
    if (campaignId) enrichContacts();
  }, [campaignId, enrichContacts]);

  const handleEditSubmit = React.useCallback(
    (values: TCampaignsFormData) => {
      if (!campaignId) return;
      updateCampaignMutation.mutate({
        id: campaignId,
        data: mapCampaignFormToUpdatePayload(values, organizationId),
      });
    },
    [campaignId, organizationId, updateCampaignMutation]
  );

  const dispatchWhatsappMutation = useMutation({
    mutationFn: async (payload: TCampaignWhatsappDispatchPayload) => {
      if (!campaignId) {
        throw new Error("Campaign id ausente");
      }
      return campaignsApi.dispatchCampaignWhatsappTemplate(campaignId, payload);
    },
    onSuccess: (data) => {
      setIsDispatchDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["campaign-details", campaignId] });
      queryClient.invalidateQueries({
        queryKey: ["campaign-analytics-summary", campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: ["campaign-dispatch-runs", campaignId],
      });
      toast({
        title: t("campaigns.details.dispatch.successTitle"),
        description: `${t("campaigns.details.dispatch.successSent")}: ${data.success_count} · ${t("campaigns.details.dispatch.successFailed")}: ${data.failed_count} · ${t("campaigns.details.dispatch.successTotal")}: ${data.total_contacts}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleDispatchPayloadConfirm = React.useCallback(
    (payload: TCampaignWhatsappDispatchPayload) => {
      openDialog(
        t("campaigns.details.dispatch.confirmTitle"),
        t("campaigns.details.dispatch.confirmDescription"),
        () => {
          dispatchWhatsappMutation.mutate(payload);
        }
      );
    },
    [dispatchWhatsappMutation, openDialog, t]
  );

  const canDispatchWhatsapp = Boolean(organizationId && contacts.length > 0);

  const handleDeleteClick = React.useCallback(() => {
    if (!campaignId || !campaign) return;
    openDialog(
      t("generic.confirmDelete"),
      campaign.name,
      () => deleteCampaignMutation.mutate(campaignId)
    );
  }, [campaign, campaignId, deleteCampaignMutation, openDialog, t]);

  const saveMarketingCostsMutation = useMutation({
    mutationFn: (vars: {
      whatsapp_unit_cost_cents: number | null;
      whatsapp_budget_cents: number | null;
    }) => campaignsApi.updateCampaign(campaignId as string, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-details", campaignId] });
      queryClient.invalidateQueries({
        queryKey: ["campaign-analytics-summary", campaignId],
      });
      toast({
        title: t("generic.success"),
        description: t("campaigns.dashboard.costSheet.success"),
      });
      setIsCostSheetOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const pauseCampaignMutation = useMutation({
    mutationFn: () =>
      campaignsApi.updateCampaign(campaignId as string, { status: "paused" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-details", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast({
        title: t("generic.success"),
        description: t("campaigns.dashboard.pauseSuccess"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resumeCampaignMutation = useMutation({
    mutationFn: () =>
      campaignsApi.updateCampaign(campaignId as string, { status: "active" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-details", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast({
        title: t("generic.success"),
        description: t("campaigns.dashboard.resumeSuccess"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleRefreshAnalytics = React.useCallback(() => {
    void refetchAnalytics();
    void refetchDispatchRuns();
  }, [refetchAnalytics, refetchDispatchRuns]);

  const handleExportDispatchRuns = React.useCallback(() => {
    if (!campaignId) {
      return;
    }
    const headers = [
      "data",
      "lote",
      "template",
      "envios",
      "entregues",
      "chats",
      "custo_centavos",
    ];
    const lines = dispatchRuns.map((r) =>
      [
        r.created_at,
        r.batch_label,
        `${r.template_name ?? ""} ${r.template_language ?? ""}`.trim(),
        String(r.sent_items),
        String(r.delivered_items),
        String(r.chats_started),
        r.estimated_cost_cents != null ? String(r.estimated_cost_cents) : "",
      ].join(";")
    );
    const bom = "\uFEFF";
    const csv = bom + [headers.join(";"), ...lines].join("\n");
    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `campanha_${campaignId}_lotes.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [campaignId, dispatchRuns]);

  const handleBack = React.useCallback(() => {
    if (clientId) {
      navigate(`/clients/${clientId}/campaigns`);
      return;
    }

    navigate("/clients");
  }, [clientId, navigate]);

  const handleTestFlow = React.useCallback((contact: TCampaignContact) => {
    setFlowTestSessionId(crypto.randomUUID());
    setSelectedContactForChat(contact);
  }, []);

  const handleChatOpenChange = React.useCallback((open: boolean) => {
    if (!open) {
      setSelectedContactForChat(null);
      setFlowTestSessionId(null);
    }
  }, []);

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4">
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="p-3"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      </div>

      {campaignId && (
        <CampaignDashboard
          campaign={campaign}
          summary={analyticsSummary}
          runs={dispatchRuns}
          isSummaryLoading={isAnalyticsLoading || isCampaignLoading}
          isRunsLoading={isDispatchRunsLoading}
          funnelDays={funnelDays}
          onFunnelDaysChange={setFunnelDays}
          onRefreshAnalytics={handleRefreshAnalytics}
          onExportRuns={handleExportDispatchRuns}
          onPauseCampaign={() => {
            if (!campaignId) {
              return;
            }
            pauseCampaignMutation.mutate();
          }}
          onResumeCampaign={() => {
            if (!campaignId) {
              return;
            }
            resumeCampaignMutation.mutate();
          }}
          isPausePending={pauseCampaignMutation.isPending}
          isResumePending={resumeCampaignMutation.isPending}
          onAdjustCosts={() => setIsCostSheetOpen(true)}
          onChangeTemplate={() => setIsEditSheetOpen(true)}
          onNewBatch={() => setIsDispatchDialogOpen(true)}
        />
      )}

      {campaignId && (
        <div className="flex flex-wrap justify-end items-center gap-2">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditSheetOpen(true)}
              disabled={!campaign}
              className="gap-2"
            >
              <Pencil className="h-4 w-4" />
              {t("generic.edit")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleDeleteClick}
              disabled={!campaign}
            >
              <Trash2 className="h-4 w-4" />
              {t("generic.delete")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsUploadModalOpen(true)}
              className="gap-2"
            >
              <Upload className="h-4 w-4" />
              {t("campaigns.upload.modal.title")}
            </Button>
          </div>
          <div
            className="h-8 w-px bg-border shrink-0"
            aria-hidden
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleEnrich}
            disabled={isEnriching || contacts.length === 0}
            className="gap-2 border-violet-500/50 bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 hover:border-violet-500/60 dark:text-violet-300 dark:bg-violet-500/15 dark:hover:bg-violet-500/25"
          >
            {isEnriching ? (
              <Sparkles className="h-4 w-4 animate-pulse" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {isEnriching
              ? t("campaigns.details.enrich.buttonLoading")
              : t("campaigns.details.enrich.button")}
          </Button>
          <Button
            type="button"
            className="gap-2"
            onClick={() => setIsDispatchDialogOpen(true)}
            disabled={
              !canDispatchWhatsapp || dispatchWhatsappMutation.isPending
            }
          >
            <Send className="h-4 w-4" />
            {dispatchWhatsappMutation.isPending
              ? t("generic.loading")
              : t("campaigns.details.dispatch.button")}
          </Button>
        </div>
      )}
      {isEnriching && (
        <div className="flex items-center gap-3 rounded-lg border border-violet-500/30 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 px-4 py-3 text-sm text-violet-800 dark:text-violet-200">
          <Sparkles className="h-5 w-5 shrink-0 animate-pulse text-violet-600 dark:text-violet-400" />
          <span className="font-medium">
            {t("campaigns.details.enrich.bannerAi")}
          </span>
          <span className="inline-flex gap-1 rounded-full bg-violet-500/20 px-2 py-0.5 text-xs font-medium text-violet-700 dark:text-violet-300">
            IA
          </span>
        </div>
      )}
      <CampaignDetailsContactsTable
        contacts={contacts}
        isLoading={isContactsLoading}
        onTestFlow={handleTestFlow}
      />

      {selectedContactForChat && campaignId && flowTestSessionId && (
        <CampaignFlowChat
          key={flowTestSessionId}
          open={!!selectedContactForChat}
          onOpenChange={handleChatOpenChange}
          contact={selectedContactForChat}
          campaignId={campaignId}
          flowSessionId={flowTestSessionId}
        />
      )}

      {campaignId && (
        <CampaignUploadLeadsModal
          open={isUploadModalOpen}
          onOpenChange={setIsUploadModalOpen}
          campaignId={campaignId}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["campaign-details-contacts", campaignId],
            });
          }}
        />
      )}

      <CampaignWhatsappDispatchDialog
        open={isDispatchDialogOpen}
        onOpenChange={setIsDispatchDialogOpen}
        organizationId={organizationId}
        campaign={campaign ?? null}
        isPending={dispatchWhatsappMutation.isPending}
        onValidSubmit={handleDispatchPayloadConfirm}
      />

      <Sheet open={isEditSheetOpen} onOpenChange={setIsEditSheetOpen}>
        <SheetContent side="right" className="sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t("campaigns.form.editTitle")}</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <CampaignForm
              open={isEditSheetOpen}
              form={editForm}
              onSubmit={handleEditSubmit}
              onCancel={() => setIsEditSheetOpen(false)}
              isEditing={true}
              projects={editProjects}
              plans={editPlans}
              isProjectsLoading={isEditProjectsLoading}
              isPlansLoading={isEditPlansLoading}
            />
          </div>
        </SheetContent>
      </Sheet>

      <CampaignMarketingCostSheet
        open={isCostSheetOpen}
        onOpenChange={setIsCostSheetOpen}
        campaign={campaign}
        isSaving={saveMarketingCostsMutation.isPending}
        onSave={(payload) => {
          if (!campaignId) {
            return;
          }
          saveMarketingCostsMutation.mutate(payload);
        }}
      />
    </div>
  );
};
